import { useState } from 'react'
import { useAuth, ProtectedRoute } from '../../contexts/AuthContext'
import { ProfileSettings } from './ProfileSettings'
import { 
  Cog6ToothIcon,
  UserGroupIcon,
  ChartBarIcon,
  UserIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline'

type SettingsTab = 'profile' | 'users' | 'analytics' | 'system'

interface SettingsPanelProps {
  className?: string
}

export function SettingsPanel({ className = '' }: SettingsPanelProps) {
  const { user, hasPermission } = useAuth()
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')
  const [registrationEnabled, setRegistrationEnabled] = useState(
    localStorage.getItem('registrationEnabled') !== 'false'
  )

  const tabs = [
    {
      id: 'profile' as SettingsTab,
      name: 'Profile',
      icon: UserIcon,
      permission: 'view_brands',
      description: 'Manage your account'
    },
    {
      id: 'users' as SettingsTab,
      name: 'User Management',
      icon: UserGroupIcon,
      permission: 'user_management',
      description: 'Manage user accounts'
    },
    {
      id: 'analytics' as SettingsTab,
      name: 'Analytics',
      icon: ChartBarIcon,
      permission: 'view_brands',
      description: 'View system analytics'
    },
    {
      id: 'system' as SettingsTab,
      name: 'System Settings',
      icon: Cog6ToothIcon,
      permission: 'system_settings',
      description: 'Configure system'
    }
  ]

  const availableTabs = tabs.filter(tab => hasPermission(tab.permission as any))

  const handleRegistrationToggle = () => {
    const newValue = !registrationEnabled
    setRegistrationEnabled(newValue)
    localStorage.setItem('registrationEnabled', newValue.toString())
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return user ? <ProfileSettings user={user} /> : null
      
      case 'users':
        return (
          <div className="space-y-6">
            {/* User Management Interface */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  System Users
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-300 mt-1">
                  Manage user accounts and permissions
                </p>
              </div>
              <div className="p-6">
                {/* Demo Users List */}
                <div className="space-y-4">
                  {[
                    { email: 'admin@brandscope.com', name: 'System Administrator', role: 'admin', status: 'active' },
                    { email: 'editor@brandscope.com', name: 'Brand Editor', role: 'editor', status: 'active' },
                    { email: 'viewer@brandscope.com', name: 'Data Viewer', role: 'viewer', status: 'active' },
                    { email: user?.email || 'current@user.com', name: user?.name || 'Current User', role: user?.role || 'viewer', status: 'active' }
                  ].filter((demoUser, index, arr) => 
                    // Remove duplicate if current user matches demo user
                    index === arr.findIndex(u => u.email === demoUser.email)
                  ).map((demoUser, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {demoUser.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {demoUser.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-300">
                            {demoUser.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          demoUser.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                          demoUser.role === 'editor' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {demoUser.role.charAt(0).toUpperCase() + demoUser.role.slice(1)}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Active
                        </span>
                        {hasPermission('user_management') && demoUser.email !== user?.email && (
                          <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <Cog6ToothIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Demo Mode:</strong> This shows sample users. In production, this would display actual registered users with full management capabilities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      
      case 'analytics':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              System Analytics
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Advanced analytics dashboard will be implemented in a future update.
            </p>
          </div>
        )
      
      case 'system':
        return (
          <div className="space-y-6">
            {/* User Registration Control */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    User Registration
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-300 mt-1">
                    Control whether new users can sign up for accounts
                  </p>
                </div>
                <button
                  onClick={handleRegistrationToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    registrationEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      registrationEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {!registrationEnabled && (
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <ShieldExclamationIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      New user registration is disabled. Only administrators can create accounts.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Additional System Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
              <Cog6ToothIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Additional System Settings
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Additional system configuration options will be implemented in a future update.
              </p>
            </div>
          </div>
        )
      
      default:
        return null
    }
  }

  return (
    <ProtectedRoute requiredPermission="view_brands">
      <div className={className}>
        {/* Settings Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Settings
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Manage your account and system configuration
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar Navigation */}
              <div className="lg:w-64 flex-shrink-0">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                  {/* User Info */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      {user?.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="h-8 w-8 rounded-full"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {user?.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user?.name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                          {user?.role} Access
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Navigation */}
                  <nav className="p-2">
                    {availableTabs.map((tab) => {
                      const IconComponent = tab.icon
                      const isActive = activeTab === tab.id
                      
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-md transition-colors ${
                            isActive
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                          }`}
                        >
                          <IconComponent className="h-4 w-4" />
                          <div>
                            <p className="text-sm font-medium">{tab.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {tab.description}
                            </p>
                          </div>
                        </button>
                      )
                    })}
                  </nav>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  )
}